/**
 * Cart Bundle Nesting
 *
 * Line items sharing the same `properties._bundle_id` (set by
 * bundle-multi-color.js when a multi-quantity tier is added) are rendered
 * as one nested group (see cart-drawer-bundle-group.liquid /
 * cart-line-item-bundle-group.liquid — Figma node 4584:2649 / 4584:5208).
 *
 * Two actions, both handled here directly via the raw Cart AJAX API, each
 * in a SINGLE network request:
 * - Trash button (`.cart-bundle-group__remove`) removes every line in the
 *   set at once.
 * - `+`/`-` stepper (`data-bundle-qty-increase` / `-decrease`) changes every
 *   line's quantity together, scaled by how many units of that colour make
 *   up one set (`data-unit-qty`, from bundle-multi-color.js's
 *   `_bundle_unit_qty` property), so the ratio between colours never
 *   drifts even when a colour was picked in more than one slot.
 *
 * Both read each line's current key/quantity/unit-qty straight off the
 * already-rendered `.cart-bundle-group__child` elements instead of
 * fetching /cart.js first — that DOM only ever reflects this same script's
 * own last section-HTML swap (see below), so it's already current. Combined
 * with asking /cart/update.js to also render the section HTML in the same
 * response (`sections`/`sections_url` params — a standard Shopify Ajax Cart
 * API feature), this whole flow is one round trip instead of the three it
 * used to be (fetch cart, update, re-fetch section HTML separately).
 *
 * Still exactly one mutation per action (not a sequence of them), which is
 * what actually matters for correctness here — this product has an
 * automatic discount tied to quantity (e.g. "2 Curvy Brush Rabat"), and a
 * line's `key` (variant_id:properties_hash) isn't stable across SEPARATE
 * mutations, so anything that changes lines one at a time (old sequential
 * /cart/change.js calls, or the DOM read here being stale relative to a
 * genuinely concurrent change) would risk acting on a stale key or an
 * unevenly-updated set. Buttons are disabled synchronously before the
 * request starts specifically to rule out the latter for this script's own
 * clicks.
 *
 * None of these use Minimog's own `.scd-item__remove` / `.scd-item__btn`
 * classes on purpose — Minimog's native delegated handlers (bound in
 * assets/app.js) act on a single line by positional index into its own
 * cached cart, so matching those classes would race this handler instead of
 * cooperating with it.
 *
 * This is fully self-contained and does NOT call into any Minimog Cart
 * internals (window.MinimogTheme.Cart, Shopify.onCartUpdate, etc.) — those
 * turned out to be unreliable here: MinimogTheme.Cart's exposure on
 * `window` depends on module-load-order details that aren't guaranteed, and
 * Shopify.onCartUpdate(cart, false) skips its own DOM refresh entirely when
 * passed `false` (it only calls Cart.renderNewCart() when the drawer-open
 * flag is true).
 */
(function () {
  'use strict';

  if (window.__cartBundleNestingBound) return;
  window.__cartBundleNestingBound = true;

  var updateUrl = (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.cart_update_url) || '/cart/update.js';

  function sectionIdFor(itemEl) {
    return itemEl.closest('#cart-drawer-container') ? 'cart-drawer' : 'cart-template';
  }

  // Reads {key, quantity, unit-qty, extraKeys} straight off the rendered DOM
  // for every slot in this bundle — no /cart.js fetch needed (see file
  // header). `extraKeys` are OTHER cart lines sharing this same slot that
  // Shopify created by itself — e.g. a "buy 2 get 1 free" style automatic
  // discount splits one colour's quantity into a paid line + a free line
  // even though it's logically still one slot. `quantity` is already the
  // SUM across all of those lines (see cart-line-item-bundle-group.liquid /
  // cart-drawer-bundle-group.liquid), so the stepper below must assign the
  // whole new total to `key` and zero out `extraKeys` rather than scaling
  // each split line independently (which would double-count the delta).
  function domBundleLines(bundleId) {
    var lines = [];
    document.querySelectorAll('.cart-bundle-group__child[data-bundle-id="' + bundleId + '"]').forEach(function (el) {
      lines.push({
        key: el.getAttribute('data-id'),
        quantity: parseInt(el.getAttribute('data-quantity'), 10) || 0,
        unitQty: parseInt(el.getAttribute('data-unit-qty'), 10) || 1,
        extraKeys: (el.getAttribute('data-extra-ids') || '').split(',').filter(Boolean),
      });
    });
    return lines;
  }

  // `updates` is a {key: quantity} map — Shopify applies every line in one
  // atomic request. `sections` asks it to also render that section's HTML
  // in the same response, rather than needing a second fetch afterward.
  function updateLines(updates, sectionId) {
    return fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: sectionId, sections_url: window.location.pathname }),
    }).then(function (res) { return res.json(); });
  }

  function applySectionHTML(data, itemEl, sectionId) {
    var html = data.sections && data.sections[sectionId];
    if (!html) return;

    var wrapper = itemEl.closest('.scd__wrapper') || document;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newBody = doc.querySelector('.scd__body');
    var newSummary = doc.querySelector('.scd__summary');
    var oldBody = wrapper.querySelector('.scd__body');
    var oldSummary = wrapper.querySelector('.scd__summary');
    if (newBody && oldBody) oldBody.replaceWith(newBody);
    if (newSummary && oldSummary) oldSummary.replaceWith(newSummary);

    document.querySelectorAll('.sf-cart-count').forEach(function (el) {
      el.textContent = data.item_count;
    });

    if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
      window.MinimogEvents.emit('ON_CART_UPDATE', data);
    }
  }

  function handleError(err) {
    console.error('[cart-bundle-nesting] Cart update failed, reloading:', err);
    window.location.reload();
  }

  // ─── Remove whole set ──────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest && e.target.closest('.cart-bundle-group__remove');
    if (!removeBtn) return;

    var itemEl = removeBtn.closest('[data-bundle-id]');
    if (!itemEl) return;

    var bundleId = removeBtn.getAttribute('data-bundle-id') || itemEl.getAttribute('data-bundle-id');
    if (!bundleId || removeBtn.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    removeBtn.disabled = true;

    var sectionId = sectionIdFor(itemEl);
    var updates = {};
    domBundleLines(bundleId).forEach(function (line) {
      updates[line.key] = 0;
      line.extraKeys.forEach(function (key) { updates[key] = 0; });
    });

    updateLines(updates, sectionId)
      .then(function (data) {
        applySectionHTML(data, itemEl, sectionId);
      })
      .catch(handleError)
      .finally(function () {
        removeBtn.disabled = false;
      });
  }, true);

  // ─── Set-count stepper (+/-) ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bundle-qty-increase], [data-bundle-qty-decrease]');
    if (!btn) return;

    var itemEl = btn.closest('[data-bundle-id]');
    if (!itemEl) return;

    var bundleId = btn.getAttribute('data-bundle-id') || itemEl.getAttribute('data-bundle-id');
    if (!bundleId || btn.disabled) return;

    var delta = btn.hasAttribute('data-bundle-qty-increase') ? 1 : -1;

    e.preventDefault();
    e.stopImmediatePropagation();

    var groupButtons = document.querySelectorAll(
      '[data-bundle-id="' + bundleId + '"][data-bundle-qty-increase], [data-bundle-id="' + bundleId + '"][data-bundle-qty-decrease]'
    );
    groupButtons.forEach(function (el) { el.disabled = true; });

    var sectionId = sectionIdFor(itemEl);
    var updates = {};
    domBundleLines(bundleId).forEach(function (line) {
      updates[line.key] = Math.max(line.unitQty, line.quantity + (delta * line.unitQty));
      line.extraKeys.forEach(function (key) { updates[key] = 0; });
    });

    updateLines(updates, sectionId)
      .then(function (data) {
        applySectionHTML(data, itemEl, sectionId);
      })
      .catch(handleError);
    // Buttons intentionally left disabled on success — the section refresh
    // replaces this whole row (with fresh disabled state, e.g. "-" disabled
    // again at 1 set) rather than re-enabling stale ones.
  }, true);
})();
